import axios from "axios";
import { pluginService, Plugin } from "@/services/plugin";

export interface WhatsAppMessageBody {
  chatId: string;
  text: string;
  session?: string;
}

export interface WhatsAppSessionInfo {
  name: string;
  status:
    | "STOPPED"
    | "STARTING"
    | "SCAN_QR_CODE"
    | "WORKING"
    | "FAILED"
    | string;
  me?: {
    id: string;
    pushName?: string;
  } | null;
  [key: string]: unknown;
}

export interface WhatsAppContact {
  id: string;
  name: string; // nombre guardado en la agenda
  pushname: string; // nombre configurado por el contacto en su whatsapp
  picture: string | null;
  lastMessagePreview: string | null;
  [key: string]: unknown;
}

export interface GetContactsParams {
  sortBy?: string;
  sortOrder?: string;
  limit?: number;
  offset?: number;
}

export interface WhatsAppChatOverviewItem {
  id: string;
  name: string | null;
  picture: string | null;
  lastMessage: {
    timestamp: number;
    body?: string;
    hasMedia?: boolean;
    location?: unknown;
    vCards?: unknown;
    [key: string]: unknown;
  } | null;
  [key: string]: unknown;
}

function getMessagePreview(
  chat: WhatsAppChatOverviewItem,
): string | null {
  const message = chat.lastMessage;
  if (!message) return null;
  if (message.body) return message.body;
  if (message.hasMedia) return "📎 Archivo multimedia";
  if (message.location) return "📍 Ubicación";
  if (message.vCards) return "👤 Contacto";
  return null;
}

export interface GetChatContactsPageParams {
  limit?: number;
  offset?: number;
}

export interface WhatsAppChatContactsPage {
  items: WhatsAppContact[];
  nextOffset: number | null;
}

export interface WhatsAppLidResult {
  lid: string;
  pn: string;
}

export enum EWhatsAppAck {
  ERROR = -1,
  PENDING = 0,
  SERVER = 1,
  DEVICE = 2,
  READ = 3,
  PLAYED = 4,
}

export interface WhatsAppMessageMedia {
  url?: string | null;
  mimetype?: string | null;
  filename?: string | null;
  error?: { message?: string; details?: string } | null;
}

export interface WhatsAppChatMessage {
  id: string;
  timestamp: number;
  from: string;
  to: string;
  fromMe: boolean;
  body: string | null;
  hasMedia: boolean;
  media?: WhatsAppMessageMedia | null;
  ack?: EWhatsAppAck;
  participant?: string | null;
  type?: string;
  subtype?: string;
  // Payload crudo del engine de WAHA (WEBJS/NOWEB/GOWS), su forma varía por engine
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _data?: Record<string, any>;
  [key: string]: unknown;
}

export type WhatsAppMediaKind = "image" | "video" | "audio" | "file";

export interface WhatsAppMediaFile {
  data: string; // base64 sin prefijo data:
  mimetype: string;
  filename: string;
}

export interface GetChatMessagesParams {
  limit?: number;
  offset?: number;
}

export interface WhatsAppEvent {
  event: string;
  session: string;
  payload: { id?: string; from?: string; to?: string; ack?: EWhatsAppAck } | null;
  [key: string]: unknown;
}

const MEDIA_ENDPOINTS: Record<WhatsAppMediaKind, string> = {
  image: "/sendImage",
  video: "/sendVideo",
  audio: "/sendVoice",
  file: "/sendFile",
};

export function getWhatsAppErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

/**
 * Convierte un teléfono (con o sin formato) al chatId de WhatsApp: "573001234567@c.us".
 */
export function phoneToChatId(phone: string): string {
  return `${phone.replace(/\D/g, "")}@c.us`;
}

const CONFIG_ERROR =
  "No hay configuración de WhatsApp activa. Por favor, comuníquese con su proveedor.";

const TYPING_MS_PER_CHAR = 50;
const TYPING_MIN_MS = 800;
const TYPING_MAX_MS = 6000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getTypingDurationMs(text: string): number {
  const estimated = text.length * TYPING_MS_PER_CHAR;
  return Math.min(TYPING_MAX_MS, Math.max(TYPING_MIN_MS, estimated));
}

async function resolvePlugin(): Promise<Plugin> {
  const result = await pluginService.getPlugins({
    name: "self.ws",
    status: "ACTIVE",
  });
  const plugin = result.data[0];
  if (!plugin) throw new Error(CONFIG_ERROR);
  return plugin;
}

function buildClient(plugin: Plugin) {
  return axios.create({
    baseURL: plugin.url,
    headers: {
      "Content-Type": "application/json",
      "x-api-key": plugin.token,
    },
  });
}

export const whatsappService = {
  async createSession(): Promise<Blob> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    const response = await client.get(`/${plugin.referenceId}/auth/qr`, {
      responseType: "blob",
    });
    return response.data;
  },

  async sendMessage(body: WhatsAppMessageBody): Promise<void> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    body.session = plugin.referenceId;

    const typingPayload = { chatId: body.chatId, session: plugin.referenceId };
    const typingDuration = getTypingDurationMs(body.text);

    await client.post(`/startTyping`, typingPayload);
    try {
      await sleep(typingDuration);
    } finally {
      await client.post(`/stopTyping`, typingPayload);
    }

    await client.post(`/sendText`, body);
  },

  async getSessionStatus(): Promise<WhatsAppSessionInfo> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    const response = await client.get(`/sessions/${plugin.referenceId}`);
    return response.data;
  },

  async startSession(): Promise<WhatsAppSessionInfo> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    const response = await client.post(`/sessions/${plugin.referenceId}/start`);
    return response.data;
  },

  async restartSession(): Promise<WhatsAppSessionInfo> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    const response = await client.post(
      `/sessions/${plugin.referenceId}/restart`,
    );
    return response.data;
  },

  async logoutSession(): Promise<WhatsAppSessionInfo> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    const response = await client.post(
      `/sessions/${plugin.referenceId}/logout`,
    );
    return response.data;
  },

  async getContacts(
    params: GetContactsParams = {},
  ): Promise<WhatsAppContact[]> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    const response = await client.get(`/contacts/all`, {
      params: { session: plugin.referenceId, ...params },
    });
    return response.data;
  },

  async getChatContactsPage({
    limit = 50,
    offset = 0,
  }: GetChatContactsPageParams = {}): Promise<WhatsAppChatContactsPage> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    const response = await client.get(
      `/${plugin.referenceId}/chats/overview`,
      { params: { merge: true, limit, offset } },
    );
    const page: WhatsAppChatOverviewItem[] = response.data;

    return {
      items: page
        .map((chat) => ({
          id: chat.id,
          name: chat.name ?? "",
          pushname: chat.name ?? "",
          picture: chat.picture ?? null,
          lastMessagePreview: getMessagePreview(chat),
        })),
      nextOffset: page.length === limit ? offset + limit : null,
    };
  },

  async getPhoneByLid(lid: string): Promise<WhatsAppLidResult> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    const response = await client.get(
      `/${plugin.referenceId}/lids/${lid}`,
    );
    return response.data;
  },

  async getLidByPhone(phone: string): Promise<WhatsAppLidResult> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    const response = await client.get(
      `/${plugin.referenceId}/lids/pn/${phone.replace(/\D/g, "")}`,
    );
    return response.data;
  },

  // Mensajes del chat ordenados del más reciente al más antiguo.
  // merge=true une los chats @lid y @c.us del mismo contacto.
  async getChatMessages(
    chatId: string,
    { limit = 20, offset = 0 }: GetChatMessagesParams = {},
  ): Promise<WhatsAppChatMessage[]> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    const response = await client.get(
      `/${plugin.referenceId}/chats/${chatId}/messages`,
      {
        params: {
          limit,
          offset,
          downloadMedia: false,
          sortBy: "messageTimestamp",
          sortOrder: "desc",
          merge: true,
        },
      },
    );
    return response.data ?? [];
  },

  async getChatMessage(
    chatId: string,
    messageId: string,
  ): Promise<WhatsAppChatMessage> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    const response = await client.get(
      `/${plugin.referenceId}/chats/${chatId}/messages/${messageId}`,
      { params: { downloadMedia: true } },
    );
    return response.data;
  },

  async downloadMedia(url: string): Promise<Blob> {
    const plugin = await resolvePlugin();
    const response = await axios.get(url, {
      responseType: "blob",
      headers: { "x-api-key": plugin.token },
    });
    return response.data;
  },

  async markChatAsRead(chatId: string): Promise<void> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    await client.post(`/${plugin.referenceId}/chats/${chatId}/messages/read`, {});
  },

  async sendChatText(chatId: string, text: string): Promise<void> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    await client.post(`/sendText`, {
      session: plugin.referenceId,
      chatId,
      text,
    });
  },

  async sendChatMedia(
    kind: WhatsAppMediaKind,
    chatId: string,
    file: WhatsAppMediaFile,
    caption?: string,
  ): Promise<void> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    const body: Record<string, unknown> = {
      session: plugin.referenceId,
      chatId,
      file,
    };
    if (kind !== "audio" && caption) body.caption = caption;
    if (kind === "audio" || kind === "video") body.convert = true;
    await client.post(MEDIA_ENDPOINTS[kind], body);
  },

  async getProfilePicture(contactId: string): Promise<string | null> {
    const plugin = await resolvePlugin();
    const client = buildClient(plugin);
    const response = await client.get(`/contacts/profile-picture`, {
      params: { session: plugin.referenceId, contactId },
    });
    return response.data?.profilePictureURL ?? null;
  },

  // URL del WebSocket de eventos de WAHA (vive en la raíz del servidor, fuera de /api).
  async getEventsSocketUrl(events: string[]): Promise<string> {
    const plugin = await resolvePlugin();
    const base = plugin.url
      .replace(/\/+$/, "")
      .replace(/\/api$/, "")
      .replace(/^http/, "ws");
    const params = new URLSearchParams();
    params.append("session", plugin.referenceId);
    events.forEach((event) => params.append("events", event));
    params.append("x-api-key", plugin.token);
    return `${base}/ws?${params.toString()}`;
  },
};
